import React, { useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';
import { useAuth } from '../context/AuthContext';
import './AdminPanel.css';

function AdminPanel() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('users');

  // Create user form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    credits: 10,
  });
  const [creating, setCreating] = useState(false);

  // Edit user modal
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    is_active: true,
    credits: 0,
  });
  const [updating, setUpdating] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Credit requests
  const [creditRequests, setCreditRequests] = useState([]);
  const [creditFilter, setCreditFilter] = useState('pending');
  const [loadingRequests, setLoadingRequests] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [usersData, statsData] = await Promise.all([
        authService.getUsers(),
        authService.getStats(),
      ]);
      setUsers(usersData);
      setStats(statsData);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCreditRequests = useCallback(async () => {
    setLoadingRequests(true);
    try {
      const data = await authService.getCreditRequests(creditFilter === 'all' ? '' : creditFilter);
      setCreditRequests(data);
    } catch (err) {
      setError('Failed to load credit requests');
    } finally {
      setLoadingRequests(false);
    }
  }, [creditFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'credits') {
      fetchCreditRequests();
    }
  }, [activeTab, fetchCreditRequests]);

  // Auto-clear messages
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(''), 4000);
      return () => clearTimeout(t);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(t);
    }
  }, [error]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      await authService.createUser(
        createForm.name,
        createForm.email,
        createForm.password,
        createForm.role,
        parseInt(createForm.credits) || 10
      );
      setSuccess(`User "${createForm.name}" created successfully`);
      setCreateForm({ name: '', email: '', password: '', role: 'user', credits: 10 });
      setShowCreateForm(false);
      fetchData();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleOpenEdit = (user) => {
    setEditUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      is_active: user.is_active,
      credits: user.credits ?? 0,
    });
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setError('');
    try {
      const data = {
        name: editForm.name,
        email: editForm.email,
        role: editForm.role,
        is_active: editForm.is_active,
        credits: parseInt(editForm.credits) || 0,
      };
      if (editForm.password) {
        data.password = editForm.password;
      }
      await authService.updateUser(editUser.id, data);
      setSuccess(`User "${editForm.name}" updated successfully`);
      setEditUser(null);
      fetchData();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to update user');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    setError('');
    try {
      await authService.deleteUser(deleteTarget.id);
      setSuccess(`User "${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await authService.updateUser(user.id, { is_active: !user.is_active });
      setSuccess(`User "${user.name}" ${user.is_active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleResolveCreditRequest = async (requestId, action) => {
    try {
      await authService.resolveCreditRequest(requestId, action, '');
      setSuccess(`Credit request ${action}d successfully`);
      fetchCreditRequests();
      fetchData(); // refresh stats
    } catch (err) {
      setError(err?.response?.data?.detail || `Failed to ${action} request`);
    }
  };

  if (loading) {
    return (
      <div className="admin-panel">
        <div className="admin-loading">
          <i className="fas fa-spinner fa-spin"></i>
          Loading admin panel...
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <div>
          <h1>Admin Panel</h1>
          <p className="admin-subtitle">Manage users and platform access</p>
        </div>
        <button
          className="xbtn xbtn-md xbtn-primary admin-create-btn"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          <i className={`fas ${showCreateForm ? 'fa-times' : 'fa-user-plus'}`}></i>
          {showCreateForm ? 'Cancel' : 'Create User'}
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="admin-alert admin-alert-error">
          <i className="fas fa-exclamation-circle"></i>
          {error}
        </div>
      )}
      {success && (
        <div className="admin-alert admin-alert-success">
          <i className="fas fa-check-circle"></i>
          {success}
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="admin-stats">
          <div className="stat-card">
            <div className="stat-icon"><i className="fas fa-users"></i></div>
            <div className="stat-info">
              <span className="stat-number">{stats.total_users}</span>
              <span className="stat-label">Total Users</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon stat-icon-green"><i className="fas fa-user-check"></i></div>
            <div className="stat-info">
              <span className="stat-number">{stats.active_users}</span>
              <span className="stat-label">Active Users</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon stat-icon-purple"><i className="fas fa-user-shield"></i></div>
            <div className="stat-info">
              <span className="stat-number">{stats.admin_count}</span>
              <span className="stat-label">Admins</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon stat-icon-orange"><i className="fas fa-film"></i></div>
            <div className="stat-info">
              <span className="stat-number">{stats.total_jobs}</span>
              <span className="stat-label">Active Jobs</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon stat-icon-yellow"><i className="fas fa-bolt"></i></div>
            <div className="stat-info">
              <span className="stat-number">{stats.pending_credit_requests || 0}</span>
              <span className="stat-label">Pending Requests</span>
            </div>
          </div>
        </div>
      )}

      {/* Tab Switcher */}
      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <i className="fas fa-users"></i> Users
        </button>
        <button
          className={`admin-tab ${activeTab === 'credits' ? 'active' : ''}`}
          onClick={() => setActiveTab('credits')}
        >
          <i className="fas fa-bolt"></i> Credit Requests
          {stats?.pending_credit_requests > 0 && (
            <span className="admin-tab-badge">{stats.pending_credit_requests}</span>
          )}
        </button>
      </div>

      {/* ===== USERS TAB ===== */}
      {activeTab === 'users' && (
        <>
          {/* Create User Form */}
          {showCreateForm && (
            <div className="admin-create-form-card">
              <h3><i className="fas fa-user-plus"></i> Create New User</h3>
              <form onSubmit={handleCreateUser} className="admin-form">
                <div className="admin-form-row">
                  <div className="admin-form-field">
                    <label>Full Name</label>
                    <input
                      type="text"
                      placeholder="John Doe"
                      value={createForm.name}
                      onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                      required
                      minLength={2}
                    />
                  </div>
                  <div className="admin-form-field">
                    <label>Email Address</label>
                    <input
                      type="email"
                      placeholder="john@example.com"
                      value={createForm.email}
                      onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="admin-form-row">
                  <div className="admin-form-field">
                    <label>Password</label>
                    <input
                      type="password"
                      placeholder="Min 6 characters"
                      value={createForm.password}
                      onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                      required
                      minLength={6}
                    />
                  </div>
                  <div className="admin-form-field">
                    <label>Role</label>
                    <select
                      value={createForm.role}
                      onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
                <div className="admin-form-row">
                  <div className="admin-form-field">
                    <label><i className="fas fa-bolt"></i> Video Credits</label>
                    <input
                      type="number"
                      placeholder="10"
                      value={createForm.credits}
                      onChange={(e) => setCreateForm({ ...createForm, credits: e.target.value })}
                      min={0}
                      max={10000}
                    />
                    <span className="field-hint">Number of videos this user can generate</span>
                  </div>
                  <div className="admin-form-field"></div>
                </div>
                <div className="admin-form-actions">
                  <button type="submit" className="xbtn xbtn-md xbtn-primary" disabled={creating}>
                    {creating ? (
                      <><i className="fas fa-spinner fa-spin"></i> Creating...</>
                    ) : (
                      <><i className="fas fa-plus"></i> Create User</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Users Table */}
          <div className="admin-table-card">
            <div className="admin-table-header">
              <h3><i className="fas fa-users"></i> All Users</h3>
              <span className="admin-table-count">{users.length} users</span>
            </div>
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Credits</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className={!u.is_active ? 'row-inactive' : ''}>
                      <td>
                        <div className="user-cell">
                          <div className="user-avatar">
                            {u.name.charAt(0).toUpperCase()}
                          </div>
                          <span>{u.name}</span>
                        </div>
                      </td>
                      <td><span className="email-text">{u.email}</span></td>
                      <td>
                        <span className={`role-badge role-${u.role}`}>
                          {u.role === 'admin' ? (
                            <><i className="fas fa-shield-alt"></i> Admin</>
                          ) : (
                            <><i className="fas fa-user"></i> User</>
                          )}
                        </span>
                      </td>
                      <td>
                        <span className={`credits-cell ${(u.credits ?? 0) === 0 ? 'credits-zero' : ''}`}>
                          <i className="fas fa-bolt"></i> {u.role === 'admin' ? '∞' : (u.credits ?? 0)}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${u.is_active ? 'status-active' : 'status-inactive'}`}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="date-cell">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="xbtn xbtn-icon-sm xbtn-accent action-btn"
                            title="Edit user"
                            onClick={() => handleOpenEdit(u)}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className={`xbtn xbtn-icon-sm action-btn ${u.is_active ? 'xbtn-warning' : 'xbtn-success-subtle'}`}
                            title={u.is_active ? 'Deactivate' : 'Activate'}
                            onClick={() => handleToggleActive(u)}
                          >
                            <i className={`fas ${u.is_active ? 'fa-ban' : 'fa-check'}`}></i>
                          </button>
                          {u.id !== currentUser?.id && (
                            <button
                              className="xbtn xbtn-icon-sm xbtn-danger action-btn"
                              title="Delete user"
                              onClick={() => setDeleteTarget(u)}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan="7" className="empty-row">No users found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ===== CREDIT REQUESTS TAB ===== */}
      {activeTab === 'credits' && (
        <div className="admin-table-card">
          <div className="admin-table-header">
            <h3><i className="fas fa-bolt"></i> Credit Requests</h3>
            <div className="credit-filter-group">
              {['pending', 'approved', 'denied', 'all'].map((f) => (
                <button
                  key={f}
                  className={`xbtn xbtn-sm ${creditFilter === f ? 'xbtn-primary' : 'xbtn-secondary'}`}
                  onClick={() => setCreditFilter(f)}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>
          {loadingRequests ? (
            <div className="admin-loading" style={{ padding: '40px' }}>
              <i className="fas fa-spinner fa-spin"></i> Loading requests...
            </div>
          ) : creditRequests.length === 0 ? (
            <div className="credit-empty">
              <i className="fas fa-inbox"></i>
              <p>No {creditFilter !== 'all' ? creditFilter : ''} credit requests</p>
            </div>
          ) : (
            <div className="credit-requests-list">
              {creditRequests.map((cr) => (
                <div key={cr.id} className={`credit-request-card cr-${cr.status}`}>
                  <div className="cr-header">
                    <div className="cr-user-info">
                      <div className="user-avatar">
                        {cr.user_name?.charAt(0).toUpperCase() || '?'}
                      </div>
                      <div>
                        <span className="cr-user-name">{cr.user_name}</span>
                        <span className="cr-user-email">{cr.user_email}</span>
                      </div>
                    </div>
                    <div className="cr-amount">
                      <i className="fas fa-bolt"></i>
                      <span>{cr.amount} credits</span>
                    </div>
                  </div>
                  <div className="cr-reason">
                    <span className="cr-reason-label">Reason:</span> {cr.reason}
                  </div>
                  <div className="cr-footer">
                    <span className="cr-date">
                      <i className="fas fa-clock"></i>
                      {new Date(cr.created_at).toLocaleString()}
                    </span>
                    <span className={`status-badge status-${cr.status}`}>
                      {cr.status}
                    </span>
                    {cr.status === 'pending' && (
                      <div className="cr-actions">
                        <button
                          className="xbtn xbtn-sm xbtn-success"
                          onClick={() => handleResolveCreditRequest(cr.id, 'approve')}
                        >
                          <i className="fas fa-check"></i> Approve
                        </button>
                        <button
                          className="xbtn xbtn-sm xbtn-danger-solid"
                          onClick={() => handleResolveCreditRequest(cr.id, 'deny')}
                        >
                          <i className="fas fa-times"></i> Deny
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Edit Modal */}
      {editUser && (
        <div className="admin-modal-overlay" onClick={() => setEditUser(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Edit User</h3>
              <button className="admin-modal-close" onClick={() => setEditUser(null)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleUpdateUser} className="admin-form">
              <div className="admin-form-field">
                <label>Full Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="admin-form-field">
                <label>Email Address</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  required
                />
              </div>
              <div className="admin-form-field">
                <label>New Password <span className="optional">(leave blank to keep current)</span></label>
                <input
                  type="password"
                  placeholder="Enter new password"
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  minLength={6}
                />
              </div>
              <div className="admin-form-row">
                <div className="admin-form-field">
                  <label>Role</label>
                  <select
                    value={editForm.role}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="admin-form-field">
                  <label>Status</label>
                  <select
                    value={editForm.is_active ? 'active' : 'inactive'}
                    onChange={(e) =>
                      setEditForm({ ...editForm, is_active: e.target.value === 'active' })
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="admin-form-field">
                <label><i className="fas fa-bolt"></i> Video Credits</label>
                <input
                  type="number"
                  value={editForm.credits}
                  onChange={(e) => setEditForm({ ...editForm, credits: e.target.value })}
                  min={0}
                  max={100000}
                />
                <span className="field-hint">Current balance: {editUser.credits ?? 0} credits</span>
              </div>
              <div className="admin-form-actions">
                <button type="button" className="xbtn xbtn-md xbtn-secondary" onClick={() => setEditUser(null)}>
                  Cancel
                </button>
                <button type="submit" className="xbtn xbtn-md xbtn-primary" disabled={updating}>
                  {updating ? (
                    <><i className="fas fa-spinner fa-spin"></i> Saving...</>
                  ) : (
                    <><i className="fas fa-save"></i> Save Changes</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="admin-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="admin-modal admin-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Delete User</h3>
              <button className="admin-modal-close" onClick={() => setDeleteTarget(null)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="delete-confirm-body">
              <i className="fas fa-exclamation-triangle delete-warning-icon"></i>
              <p>
                Are you sure you want to delete <strong>{deleteTarget.name}</strong> ({deleteTarget.email})?
                This action cannot be undone.
              </p>
            </div>
            <div className="admin-form-actions">
              <button className="xbtn xbtn-md xbtn-secondary" onClick={() => setDeleteTarget(null)}>
                Cancel
              </button>
              <button className="xbtn xbtn-md xbtn-danger-solid" onClick={handleDeleteUser}>
                <i className="fas fa-trash"></i> Delete User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
